import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout } from "@/components/site/PageLayout";
import {
  FeatureGrid,
  DeliverablesList,
  ProcessSteps,
  ServiceCta,
} from "@/components/site/ServiceBlocks";
import { FaqSection } from "@/components/site/Faq";
import { ldScript, serviceSchema, faqSchema } from "@/lib/schema";
import screenPoolAsset from "@/assets/screen-enclosure-pool.jpg.asset.json";
import screenExteriorAsset from "@/assets/screen-enclosure-exterior.jpg.asset.json";

const heroScreen = screenPoolAsset.url;
const svcScreen = screenExteriorAsset.url;

const FAQS = [
  {
    q: "What kinds of aluminum structures do you engineer?",
    a: "Pool cages, patio and lanai enclosures, screen rooms, carports, canopies, pergolas, aluminum roof systems, and custom framed structures attached to or freestanding from the building.",
  },
  {
    q: "Do your drawings include wind load calculations?",
    a: "Yes. Every set is engineered for the site's design wind speed, exposure category, and risk category, with member sizing, connection details, and anchorage to the host structure or foundation.",
  },
  {
    q: "Can you work from an existing product approval?",
    a: "We can build a site-specific package around a manufacturer's Florida Product Approval or Miami-Dade NOA, or engineer the structure from scratch when the installation falls outside the approval limits.",
  },
  {
    q: "How fast can you turn a screen enclosure package?",
    a: "Most standard enclosure packages turn in a few business days. Larger custom aluminum structures scope up front based on span, height, and attachment complexity.",
  },
];

export const Route = createFileRoute("/screen-enclosures")({
  head: () => ({
    ...pageHead({
      title: "Screen Enclosure Engineering · SunSmart Engineering",
      description:
        "Sealed engineering for pool cages, lanai and patio enclosures, carports, pergolas and aluminum structures. Wind load, anchorage and permit-ready drawings.",
      path: "/screen-enclosures",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "Screen Enclosures & Custom Aluminum Structures",
          description:
            "Engineering and sealed permit drawings for screen enclosures, pool cages, carports, pergolas, and custom aluminum structures.",
          url: "/screen-enclosures",
          serviceType: "Structural Engineering",
        }),
      ),
      ldScript(faqSchema(FAQS)),
    ],
  }),
  component: ScreenEnclosuresPage,
});

function ScreenEnclosuresPage() {
  return (
    <PageLayout
      kicker="Construction Services · Aluminum Construction"
      title="Screen Enclosures & Custom Aluminum"
      intro="Aluminum construction is its own discipline, and it lives or dies on the details. We engineer screen enclosures, pool cages, carports, pergolas, and custom framed structures with the member sizing, connection details, and anchorage that plan reviewers expect the first time through."
    >
      <ProcessSteps
        label="01"
        title={
          <>
            What we <span className="text-sun">engineer.</span>
          </>
        }
        image={heroScreen}
        imageAlt="Aluminum screen enclosure framing over a residential pool"
        steps={[
          {
            title: "Pool Cages & Screen Rooms",
            description:
              "Mansard, gable, dome, and flat-pan enclosures sized for the site's wind speed, including gutter attachment, super gutters, and kickplate details.",
          },
          {
            title: "Patio, Lanai & Sunroom Framing",
            description:
              "Insulated and non-insulated roof panels, beams, columns, and host-structure attachment engineered as one continuous load path.",
          },
          {
            title: "Carports, Canopies & Pergolas",
            description:
              "Freestanding and attached aluminum structures with foundation design, uplift resistance, and connection detailing.",
          },
          {
            title: "Custom Aluminum Assemblies",
            description:
              "Railings, screen walls, trellises, equipment screening, and one-off framed structures that fall outside any standard product approval.",
          },
        ]}
      />

      <DeliverablesList
        label="02"
        title={
          <>
            What's in the <span className="text-sun">package.</span>
          </>
        }
        image={svcScreen}
        imageAlt="Screen enclosure attached to a residential home"
        items={[
          {
            title: "Site-Specific Wind Load Analysis",
            description:
              "Design wind speed, exposure category, risk category, and enclosure classification applied to your actual address, not a generic table.",
          },
          {
            title: "Member Sizing & Framing Plans",
            description:
              "Beam, column, purlin, and chair rail sizing with spans and spacing called out clearly for the crew in the field.",
          },
          {
            title: "Connection & Anchorage Details",
            description:
              "Fastener schedules, embedment, host-structure attachment, and slab or footing requirements, all detailed for inspection.",
          },
          {
            title: "Signed & Sealed Drawings",
            description:
              "Permit-ready sets sealed for the jurisdiction, with revisions handled quickly when plan review comes back with comments.",
          },
        ]}
      />

      <FeatureGrid
        label="03"
        theme="surface"
        title={
          <>
            Built for the <span className="text-sun">contractor.</span>
          </>
        }
        columns={2}
        items={[
          {
            title: "Fast Turnarounds",
            description:
              "Standard enclosure packages come back in days, so your permit application is not waiting on engineering.",
          },
          {
            title: "Plan Review Support",
            description:
              "If a reviewer pushes back, we respond directly with calculations and revised details until the permit is issued.",
          },
        ]}
      />

      <FaqSection
        items={FAQS}
        title={
          <>
            Aluminum construction <span className="text-sun">questions.</span>
          </>
        }
      />

      <ServiceCta
        label="04"
        title={
          <>
            Send us your <span className="text-sun">next enclosure.</span>
          </>
        }
        intro="Share the site address, a sketch or survey, and the structure you have in mind. We will scope the engineering package and get you a turnaround the same day."
        image={heroScreen}
        imageAlt="Completed aluminum screen enclosure at dusk"
      />
    </PageLayout>
  );
}
